import { resolveAmountDueNow, shouldShowLiveInvoicePreview, shouldUseDiscountedPreview, sumDedicatedEnvCents } from 'lib/billing';
import { useCheckoutState } from './useCheckoutCompat';
import { useCheckoutStore } from './useCheckoutStore';
import { useInvoicePreview } from './useInvoicePreviewStore';

export interface AmountDueNow {
  /** What Stripe will actually charge today (cents): preview amount_due for an
   *  on-cycle sub, catalog total otherwise, minus credit, $0 on trial. */
  dueCents: number;
  showLiveInvoice: boolean;
  trialApplies: boolean;
  availableCredit: number;
  /** Adding new Dedicated Env(s) to an existing account: the "modify" preview
   *  can come back empty, so the charge is computed from the catalog instead. */
  dedicatedOnlyModify: boolean;
  newDedicatedCents: number;
  /** Discount-scoped relaxation of the live-preview gate: an applied code
   *  whose settled PreviewCheckout reply carries a real Stripe discount on a
   *  non-trial cart WITHOUT a live invoice (first-time buyers — no anchor
   *  sub). `dueCents` already uses the preview's real figure in this state;
   *  TotalSummary uses the flag to render the matching Subtotal/Discount
   *  ledger so no surface shows the undiscounted catalog total. */
  discountedPreviewApplies: boolean;
}

/**
 * Single source of truth for the "due today" figure, shared by the order
 * summary (TotalSummary) and the payment step (PaymentStep). Keeping one
 * derivation avoids the two surfaces disagreeing — e.g. PaymentStep hiding the
 * crypto rail for a $0 invoice that the summary still shows as payable.
 */
export function useAmountDueNow(): AmountDueNow {
  const state = useCheckoutState();
  const { preview, loading: previewLoading, quotedCoupon } = useInvoicePreview();
  // Applied discount code (store-held; the preview re-quotes with it). Only
  // used for the discount-scoped preview relaxation below — validation stays
  // display-only and purchase payloads are built elsewhere.
  const coupon = useCheckoutStore((s) => s.coupon);

  const showLiveInvoice = shouldShowLiveInvoicePreview({
    authCheckout: state.authCheckout,
    activeSubscription: state.activeSubscription,
    invoicePreview: preview,
    invoiceLoading: previewLoading
  });
  const trialApplies = Boolean((state as any).trialApplies);
  const availableCredit = Number((state.billingOverview as any)?.availableCredit || 0);
  // Only count the code as applied once the settled preview was actually
  // QUOTED with it — right after Apply the store still holds the previous
  // (couponless or different-code) quote for a debounce+network window, and
  // pricing off that stale reply would flash wrong figures.
  // The cancel offer is billing-manager's own discount, so the quote that carries it is always current.
  const couponApplied = (Boolean(coupon) && quotedCoupon === coupon) || Boolean(preview?.cancelOfferApplied);
  const grandTotalCents = resolveAmountDueNow({
    authCheckout: Boolean(state.authCheckout),
    activeSubscription: state.activeSubscription ?? null,
    invoicePreview: preview,
    invoiceLoading: previewLoading,
    grandTotalCycleCents: state.grandTotalCycleCents || 0,
    availableCredit,
    trialApplies,
    couponApplied
  });
  // Mirrors the branch resolveAmountDueNow just took for the new-user path,
  // so the ledger rows TotalSummary renders always agree with `dueCents`.
  const discountedPreviewApplies =
    !showLiveInvoice &&
    shouldUseDiscountedPreview({ couponApplied, trialApplies, invoicePreview: preview, invoiceLoading: previewLoading });

  const paidMonths = Number((state as any).paidMonths) || 1;
  // Only brand-new envs are charged now; each priced by its kind (CFD vs Futures).
  const newDedicatedCents = sumDedicatedEnvCents(
    {
      envs: state.dedicatedEnvs || [],
      kinds: state.dedicatedEnvKinds || [],
      subIds: state.dedicatedEnvSubIds || []
    },
    (state as any).billingCycle,
    paidMonths,
    { skipOwned: true }
  );
  const dedicatedOnlyModify = Boolean(state.isModifyingExisting) && !showLiveInvoice && newDedicatedCents > 0;
  const dueCents = dedicatedOnlyModify ? newDedicatedCents : grandTotalCents;

  return { dueCents, showLiveInvoice, trialApplies, availableCredit, dedicatedOnlyModify, newDedicatedCents, discountedPreviewApplies };
}
